"use client";

import { type ReactNode } from "react";
import type {
  CurrencyFormatSettings,
  Permalinks,
  TranslationEntries,
} from "@tripcart/shared";
import type { StorefrontFormatSettings } from "@tripcart/shared/format";
import { CartProvider } from "@/lib/cart/cart-context";
import { CurrencyFormatProvider } from "@/lib/currency-format-context";
import { DateTimeFormatProvider } from "@/lib/datetime-format-context";
import { PermalinksProvider } from "@/lib/permalinks-context";
import { TranslationProvider } from "@/lib/translation-context";
import type { Cart } from "@/lib/cart/types";

/**
 * Client-side provider stack. Public storefront — no auth provider, no theme
 * toggle.
 *
 * Cart is now cookie-backed: the root layout reads the signed cookie on the
 * server and passes the resulting Promise here. `CartProvider` calls `use()`
 * to unwrap it, then `useOptimistic` powers instant UI updates with Server
 * Actions writing the truth.
 *
 * TanStack Query was removed (zero usage across the app) saving ~13 KB gz
 * on every page. Future async-state surfaces use React 19 native primitives
 * (useOptimistic, useTransition, useActionState) plus RSC `fetch` with
 * Next.js cache directives.
 */
export function Providers({
  children,
  cartPromise,
  currencySettings,
  dateTimeSettings,
  permalinks,
  translationEntries,
}: {
  children: ReactNode;
  cartPromise: Promise<Cart>;
  currencySettings: CurrencyFormatSettings;
  dateTimeSettings: StorefrontFormatSettings;
  permalinks?: Permalinks;
  translationEntries: TranslationEntries;
}) {
  return (
    <TranslationProvider entries={translationEntries}>
      <CurrencyFormatProvider settings={currencySettings}>
        <DateTimeFormatProvider settings={dateTimeSettings}>
          <PermalinksProvider permalinks={permalinks}>
            <CartProvider cartPromise={cartPromise}>{children}</CartProvider>
          </PermalinksProvider>
        </DateTimeFormatProvider>
      </CurrencyFormatProvider>
    </TranslationProvider>
  );
}
